import { z } from 'zod';
import {
  CRITERION_KEY_PATTERN,
  CYCLE_STATUSES,
  GOAL_STATUSES,
  MAX_CRITERIA,
  MAX_PLAN_OBJECTIVES,
  MAX_SCALE,
  MIN_SCALE,
  PLAN_STATUSES,
  PERF_RECOMMENDATIONS,
  REVIEW_STATUSES,
} from './performance.js';

/** مخططات إدارة الأداء. */

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ بصيغة سنة-شهر-يوم.');

const optionalDateOnly = dateOnly
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

/* ==========================================================================
   الدورة
   ========================================================================== */

/**
 * معيارٌ في دورة.
 *
 * المفتاحُ بصيغةٍ ثابتةٍ لأنّه يُكتب في عمود الدرجات: مفتاحٌ فيه مسافةٌ أو
 * حرفٌ عربيٌّ يعمل اليوم ويكسر أوّلَ تصديرٍ أو تقريرٍ يُكتب بعده.
 */
export const CriterionSchema = z.object({
  key: z
    .string()
    .trim()
    .toUpperCase()
    .regex(
      CRITERION_KEY_PATTERN,
      'مفتاح المعيار حروفٌ إنجليزيّةٌ كبيرةٌ وأرقامٌ وشرطةٌ سفليّة.',
    ),
  label: z.string().trim().min(2, 'اسم المعيار قصير.').max(120),
  weight: z.coerce
    .number()
    .int('الوزن رقم صحيح.')
    .min(0, 'الوزن صفرٌ فأعلى.')
    .max(100, 'الوزن مئةٌ فأدنى.')
    .default(0),
  description: z
    .string()
    .trim()
    .max(500)
    .transform((v) => (v === '' ? undefined : v))
    .optional(),
});
export type CriterionInput = z.infer<typeof CriterionSchema>;

/**
 * المعاييرُ لا تتكرّر مفاتيحُها.
 *
 * درجاتُ التقييم مخزَّنةٌ بمفتاح المعيار، ومفتاحان متساويان يعنيان درجةً
 * واحدةً تُقرأ لمعيارين — وتُحسب مرّتين في المحصّلة.
 */
const criteriaList = z
  .array(CriterionSchema)
  .max(MAX_CRITERIA, `المعايير أكثر من ${MAX_CRITERIA}.`)
  .refine(
    (list) => new Set(list.map((c) => c.key)).size === list.length,
    'مفتاحُ معيارٍ مكرّر.',
  );

export const UpsertCycleSchema = z
  .object({
    name: z.string().trim().min(2, 'اسم الدورة قصير.').max(120),
    startsOn: dateOnly,
    endsOn: dateOnly,
    dueOn: optionalDateOnly,
    criteria: criteriaList.default([]),
    scaleMax: z.coerce
      .number()
      .int('السُّلَّم رقم صحيح.')
      .min(MIN_SCALE, `السُّلَّم ${MIN_SCALE} درجاتٍ فأعلى.`)
      .max(MAX_SCALE, `السُّلَّم ${MAX_SCALE} درجاتٍ فأدنى.`)
      .default(5),
    notes: optionalText(2000),
  })
  .refine((v) => v.endsOn >= v.startsOn, {
    message: 'نهاية المدّة قبل بدايتها.',
    path: ['endsOn'],
  });
export type UpsertCycleInput = z.infer<typeof UpsertCycleSchema>;

/**
 * تعديلُ الدورة.
 *
 * يُكتب بيده ولا يُشتقّ بـ .partial(): المخطّطُ أعلاه صار ZodEffects بسبب
 * .refine()، و .partial() لا تعمل عليه — تُسقط الشرطَ أو تنفجر.
 */
export const UpdateCycleSchema = z
  .object({
    name: z.string().trim().min(2, 'اسم الدورة قصير.').max(120).optional(),
    startsOn: dateOnly.optional(),
    endsOn: dateOnly.optional(),
    dueOn: optionalDateOnly,
    criteria: criteriaList.optional(),
    scaleMax: z.coerce
      .number()
      .int('السُّلَّم رقم صحيح.')
      .min(MIN_SCALE)
      .max(MAX_SCALE)
      .optional(),
    notes: optionalText(2000),
  })
  .refine(
    (v) =>
      v.startsOn === undefined ||
      v.endsOn === undefined ||
      v.endsOn >= v.startsOn,
    { message: 'نهاية المدّة قبل بدايتها.', path: ['endsOn'] },
  );
export type UpdateCycleInput = z.infer<typeof UpdateCycleSchema>;

export const CycleStatusSchema = z.object({
  status: z.enum(CYCLE_STATUSES),
});
export type CycleStatusInput = z.infer<typeof CycleStatusSchema>;

export const CyclesQuerySchema = z.object({
  status: z.enum(CYCLE_STATUSES).optional(),
});
export type CyclesQuery = z.infer<typeof CyclesQuerySchema>;

/* ==========================================================================
   الأهداف
   ========================================================================== */

export const UpsertGoalSchema = z.object({
  employeeId: Cuid,
  cycleId: Cuid.nullable().optional(),
  title: z.string().trim().min(3, 'عنوان الهدف قصير.').max(200),
  description: optionalText(2000),
  metric: optionalText(200),
  target: optionalText(200),
  weight: z.coerce.number().int().min(0).max(100).default(0),
  progress: z.coerce
    .number()
    .int('ما بلغه رقم صحيح.')
    .min(0, 'ما بلغه صفرٌ فأعلى.')
    .max(100, 'ما بلغه مئةٌ فأدنى.')
    .default(0),
  status: z.enum(GOAL_STATUSES).default('OPEN'),
  dueOn: optionalDateOnly,
  outcome: optionalText(1000),
});
export type UpsertGoalInput = z.infer<typeof UpsertGoalSchema>;

export const GoalsQuerySchema = z.object({
  employeeId: Cuid.optional(),
  cycleId: Cuid.optional(),
  status: z.enum(GOAL_STATUSES).optional(),
});
export type GoalsQuery = z.infer<typeof GoalsQuerySchema>;

/* ==========================================================================
   التقييم
   ========================================================================== */

export const CreateReviewSchema = z.object({
  cycleId: Cuid,
  employeeId: Cuid,
  reviewerId: Cuid.nullable().optional(),
});
export type CreateReviewInput = z.infer<typeof CreateReviewSchema>;

/**
 * كتابةُ تقييم.
 *
 * الدرجاتُ سجلٌّ مفتوحُ المفاتيح لأنّ المعايير بياناتُ الشركة لا كتالوجٌ
 * في الشيفرة — ومفاتيحُها تُفحص في الخادم من معايير الدورة نفسِها،
 * والدرجةُ تُفحص من سُلَّمها. ومخطّطٌ هنا يعدّ مفاتيحَ ثابتةً كان سيمنع
 * كلَّ معيارٍ تكتبه الشركة.
 */
export const UpdateReviewSchema = z.object({
  scores: z.record(z.coerce.number()).optional(),
  strengths: optionalText(2000),
  improvements: optionalText(2000),
  summary: optionalText(4000),
  recommendation: z.enum(PERF_RECOMMENDATIONS).nullable().optional(),
  reviewerId: Cuid.nullable().optional(),
});
export type UpdateReviewInput = z.infer<typeof UpdateReviewSchema>;

export const ReviewStatusSchema = z.object({
  status: z.enum(REVIEW_STATUSES),
  /** تعليقُ صاحب الملفّ عند الإقرار. */
  employeeNote: optionalText(2000),
});
export type ReviewStatusInput = z.infer<typeof ReviewStatusSchema>;

export const ReviewsQuerySchema = z.object({
  cycleId: Cuid.optional(),
  employeeId: Cuid.optional(),
  status: z.enum(REVIEW_STATUSES).optional(),
});
export type ReviewsQuery = z.infer<typeof ReviewsQuerySchema>;

/* ==========================================================================
   خطط التحسين
   ========================================================================== */

const objectives = z
  .array(
    z.object({
      text: z.string().trim().min(3, 'بندُ الخطّة قصير.').max(300),
      done: z.boolean().default(false),
    }),
  )
  .max(MAX_PLAN_OBJECTIVES, `البنود أكثر من ${MAX_PLAN_OBJECTIVES}.`);

export const UpsertPlanSchema = z
  .object({
    employeeId: Cuid,
    reviewId: Cuid.nullable().optional(),
    ownerId: Cuid.nullable().optional(),
    reason: z.string().trim().min(10, 'اكتب سببًا مفهومًا.').max(2000),
    startsOn: dateOnly,
    endsOn: dateOnly,
    objectives: objectives.default([]),
  })
  .refine((v) => v.endsOn >= v.startsOn, {
    message: 'نهاية المدّة قبل بدايتها.',
    path: ['endsOn'],
  });
export type UpsertPlanInput = z.infer<typeof UpsertPlanSchema>;

export const UpdatePlanSchema = z
  .object({
    ownerId: Cuid.nullable().optional(),
    reason: z
      .string()
      .trim()
      .min(10, 'اكتب سببًا مفهومًا.')
      .max(2000)
      .optional(),
    startsOn: dateOnly.optional(),
    endsOn: dateOnly.optional(),
    objectives: objectives.optional(),
  })
  .refine(
    (v) =>
      v.startsOn === undefined ||
      v.endsOn === undefined ||
      v.endsOn >= v.startsOn,
    { message: 'نهاية المدّة قبل بدايتها.', path: ['endsOn'] },
  );
export type UpdatePlanInput = z.infer<typeof UpdatePlanSchema>;

/**
 * إغلاقُ خطّة.
 *
 * الخلاصةُ مطلوبةٌ عند الإغلاق: خطّةٌ تُغلق بكلمةٍ واحدةٍ لا تصلح سندًا في
 * نزاعٍ بعد شهرين، ولا يعرف من يقرؤها لاحقًا لماذا انتهت إلى ما انتهت.
 */
export const ClosePlanSchema = z.object({
  status: z.enum(PLAN_STATUSES),
  outcome: z.string().trim().min(10, 'اكتب خلاصةً مفهومة.').max(2000),
});
export type ClosePlanInput = z.infer<typeof ClosePlanSchema>;

export const PlansQuerySchema = z.object({
  employeeId: Cuid.optional(),
  status: z.enum(PLAN_STATUSES).optional(),
});
export type PlansQuery = z.infer<typeof PlansQuerySchema>;
